import React, { useRef, useEffect, useMemo } from 'react';
import { ButtonSelect, Block, Row, IconName, Button, ButtonGroup, Icon, Flex } from '@upsales/components';
import { useDetectContainerOverflow } from '../hooks';
import BemClass from '@upsales/components/Utils/bemClass';
import T from 'Components/Helpers/translate';
import './HistoryLogFilterButtons.scss';

type ButtonConfig<TType extends string> = { title: string; value: TType; icon?: IconName };

type SingleSelectProps<TType extends string> = {
	multiSelect?: false;
	historyType: TType;
	setHistoryType: StateSetter<TType>;
	selectedFilters?: never;
	onFilterChange?: never;
	focusedFilter?: never;
	onFocusChange?: never;
};

type MultiSelectProps<TType extends string> = {
	multiSelect: true;
	selectedFilters: TType[];
	onFilterChange: (value: TType) => void;
	/** The type currently shown on its own, if any. Omit to leave the pills hide-only. */
	focusedFilter?: TType | null;
	onFocusChange?: (value: TType) => void;
	historyType?: never;
	setHistoryType?: never;
};

type Props<TType extends string> = {
	containerRef: React.RefObject<HTMLDivElement>;
	buttons: readonly ButtonConfig<TType>[];
	metadata: { [k in TType]?: number };
} & (SingleSelectProps<TType> | MultiSelectProps<TType>);

const HistoryLogFilterButtons = <TType extends string>(props: Props<TType>) => {
	const { containerRef, metadata, buttons, multiSelect } = props;

	const buttonSelectRef = useRef<HTMLDivElement>(null);
	const classes = new BemClass('HistoryLogFilterButtons');

	const {
		thresholdMet: isSmall,
		measured,
		recalculate
	} = useDetectContainerOverflow({
		containerRef,
		contentRefs: [buttonSelectRef],
		threshold: 240
	});
	const style = measured ? undefined : { visibility: 'hidden' as const };

	useEffect(() => {
		recalculate();
	}, [metadata]);

	const filteredButtons = useMemo(
		() =>
			buttons.flatMap(obj => {
				if (!metadata[obj.value]) return [];
				return [
					{
						...obj,
						label: obj.title,
						title: `${isSmall && !(obj.value === 'all') ? '' : obj.title} (${metadata[obj.value]})`
					}
				];
			}),
		[buttons, metadata, isSmall]
	);

	// Multi-select mode
	if (multiSelect) {
		const { selectedFilters, onFilterChange, focusedFilter = null, onFocusChange } = props;
		const nothingFiltered = !focusedFilter && selectedFilters.includes('all' as TType);

		return (
			<Row ref={buttonSelectRef} noWrap style={style}>
				<Block space="mlm">
					<Flex gap="u1" wrap="wrap" className={classes.b()}>
						{filteredButtons.map(button => {
							const isAll = button.value === 'all';
							const isExcluded = !isAll && !selectedFilters.includes(button.value);
							const isFocused = focusedFilter === button.value;

							const labelButton = (
								<Button
									key={button.value}
									size="sm"
									type="lined"
									color="grey"
									onClick={() => onFilterChange(button.value)}
									// "All" is a reset, not a hide - it gets no hide/show tooltip.
									title={
										isAll
											? undefined
											: isExcluded
											? T('historyLogs.filter.show', { type: button.label })
											: T('historyLogs.filter.hide', { type: button.label })
									}
									className={classes
										.elem('button')
										.mod({ excluded: isExcluded, lit: isAll && nothingFiltered })
										.b()}
								>
									{button.icon ? <Icon name={button.icon} space="mrs" /> : null}
									{button.title}
								</Button>
							);

							// The caller decides whether the isolate is offered at all. Not
							// isSmall: the React client card measures as small at 1600px (its
							// labels are already bare counts), so that would drop the target on
							// the very surface this is for. Only the drawer opts out.
							if (!onFocusChange || isAll) {
								return labelButton;
							}

							return (
								<ButtonGroup
									key={button.value}
									className={classes
										.elem('pill')
										.mod({ dimmed: !!focusedFilter && !isFocused })
										.b()}
								>
									{labelButton}
									<Button
										size="sm"
										// Solid rather than a new hue: the row signals "on" by filling,
										// as the notes button beside it does.
										type={isFocused ? undefined : 'lined'}
										color="grey"
										disabled={isExcluded}
										onClick={() => onFocusChange(button.value)}
										title={
											isFocused
												? T('default.showAll')
												: T('historyLogs.filter.only', { type: button.label })
										}
										className={classes.elem('focus').mod({ excluded: isExcluded }).b()}
									>
										<Icon name="eye" />
									</Button>
								</ButtonGroup>
							);
						})}
					</Flex>
				</Block>
			</Row>
		);
	}

	// Single-select mode (backward compatible)
	const { historyType, setHistoryType } = props;

	return (
		<Row ref={buttonSelectRef} noWrap style={style}>
			<Block space="mlm">
				<ButtonSelect size="sm" value={historyType} onChange={setHistoryType} options={filteredButtons} />
			</Block>
		</Row>
	);
};

export default HistoryLogFilterButtons;
